import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Download,
  RotateCcw,
  Loader2,
  CheckCircle2,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { queryKeys } from '@/lib/query-keys';

interface LeadEntry {
  leadgen_id: string;
  created_time: string | null;
  field_data: Array<{ name: string; values: string[] }>;
  status: 'processed' | 'unprocessed' | 'error';
  deal_id: string | null;
  contact_id: string | null;
  facebook_form_id?: string;
  mapping_profile_id?: string;
  mapping_profile_name?: string;
}

function mergeLeadsByRecency(prev: LeadEntry[], incoming: LeadEntry[]): LeadEntry[] {
  const byId = new Map<string, LeadEntry>();
  for (const l of prev) byId.set(l.leadgen_id, l);
  for (const l of incoming) byId.set(l.leadgen_id, l);
  const merged = [...byId.values()];
  merged.sort((a, b) => {
    const ta = a.created_time ? Date.parse(a.created_time) : 0;
    const tb = b.created_time ? Date.parse(b.created_time) : 0;
    return tb - ta;
  });
  return merged;
}

interface LeadListPanelProps {
  /** Load leads from all active Facebook mapping profiles (distinct form IDs). */
  unified?: boolean;
  /** Single-form mode: Facebook Lead Form ID from the selected profile. */
  facebookFormId?: string;
}

export default function LeadListPanel({ unified = false, facebookFormId = '' }: LeadListPanelProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const dateLocale = i18n.language === 'sk' ? 'sk-SK' : i18n.language === 'hu' ? 'hu-HU' : 'en-GB';

  const [leads, setLeads] = useState<LeadEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [afterByForm, setAfterByForm] = useState<Record<string, string | null>>({});
  const [hasMoreUnified, setHasMoreUnified] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [retrying, setRetrying] = useState<Set<string>>(new Set());
  const [retryResults, setRetryResults] = useState<Record<string, 'success' | 'error'>>({});
  const [expandedLead, setExpandedLead] = useState<string | null>(null);

  const fetchLeads = useCallback(
    async (opts?: { singleCursor?: string; cursorsByForm?: Record<string, string | null> }) => {
      if (!unified && !facebookFormId.trim()) {
        toast({
          title: t('leadList.fetchError'),
          description: t('leadList.noFormId'),
          variant: 'destructive',
        });
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams();
        let append = false;

        if (unified) {
          params.set('unified', '1');
          params.set('per_form_limit', '25');
          if (opts?.cursorsByForm && Object.keys(opts.cursorsByForm).length > 0) {
            params.set('cursors', JSON.stringify(opts.cursorsByForm));
            append = true;
          }
        } else {
          params.set('form_id', facebookFormId);
          params.set('limit', '25');
          if (opts?.singleCursor) {
            params.set('after', opts.singleCursor);
            append = true;
          }
        }

        const res = await fetch(`/api/admin/facebook-lead-list?${params}`, {
          credentials: 'include',
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          toast({
            title: t('leadList.fetchError'),
            description: data.message || data.error,
            variant: 'destructive',
          });
          return;
        }

        const batch: LeadEntry[] = (data.leads || []) as LeadEntry[];
        if (append) {
          setLeads((prev) => mergeLeadsByRecency(prev, batch));
        } else {
          setLeads(batch);
        }

        if (unified) {
          setAfterByForm((data.paging?.after_by_form as Record<string, string | null>) ?? {});
          setHasMoreUnified(!!data.has_more);
          setNextCursor(null);
        } else {
          setNextCursor(data.paging?.after ?? null);
          setAfterByForm({});
          setHasMoreUnified(false);
        }
        setLoaded(true);
      } catch {
        toast({ title: t('leadList.fetchError'), variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    },
    [unified, facebookFormId, t, toast]
  );

  const toggleSelect = (leadgenId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(leadgenId)) {
        next.delete(leadgenId);
      } else {
        next.add(leadgenId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const selectable = leads.filter((l) => l.status !== 'error' || !l.deal_id);
    if (selected.size === selectable.length && selectable.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectable.map((l) => l.leadgen_id)));
    }
  };

  const retryLead = async (leadgenId: string, forceNewQuote = false) => {
    setRetrying((prev) => new Set(prev).add(leadgenId));
    try {
      const res = await fetch('/api/admin/facebook-lead-retry', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ leadgen_id: leadgenId, force_new_quote: forceNewQuote }),
      });
      if (res.ok) {
        setRetryResults((prev) => ({ ...prev, [leadgenId]: 'success' }));
        setLeads((prev) =>
          prev.map((l) =>
            l.leadgen_id === leadgenId ? { ...l, status: 'processed' as const } : l
          )
        );
        void queryClient.invalidateQueries({ queryKey: queryKeys.crmBoard });
      } else {
        setRetryResults((prev) => ({ ...prev, [leadgenId]: 'error' }));
      }
    } catch {
      setRetryResults((prev) => ({ ...prev, [leadgenId]: 'error' }));
    } finally {
      setRetrying((prev) => {
        const next = new Set(prev);
        next.delete(leadgenId);
        return next;
      });
    }
  };

  const retrySelected = async () => {
    const ids = Array.from(selected);
    for (const id of ids) {
      const lead = leads.find((l) => l.leadgen_id === id);
      await retryLead(id, lead?.status === 'processed');
    }
    setSelected(new Set());
    toast({ title: t('leadList.retryBatchDone', { count: ids.length }) });
  };

  const getFieldPreview = (lead: LeadEntry): string => {
    const email = lead.field_data.find((f) => f.name.toLowerCase() === 'email')?.values[0];
    const name = lead.field_data.find((f) =>
      ['full_name', 'name', 'first_name'].includes(f.name.toLowerCase())
    )?.values[0];
    return [name, email].filter(Boolean).join(' - ') || lead.leadgen_id;
  };

  const unprocessedCount = leads.filter((l) => l.status === 'unprocessed').length;

  if (!loaded) {
    return (
      <div className="bg-card border rounded-lg p-6">
        <div className="flex flex-col items-center gap-3 py-4">
          <p className="text-sm text-muted-foreground">
            {unified ? t('leadList.descriptionUnified') : t('leadList.description')}
          </p>
          <Button onClick={() => fetchLeads()} disabled={loading} className="gap-2">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {loading ? t('common.loading') : t('leadList.fetchLeads')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border rounded-lg p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">{t('leadList.title')}</h3>
          <p className="text-sm text-muted-foreground mt-0.5">
            {unified
              ? t('leadList.countUnified', { total: leads.length, unprocessed: unprocessedCount })
              : t('leadList.count', { total: leads.length, unprocessed: unprocessedCount })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <Button
              onClick={retrySelected}
              disabled={retrying.size > 0}
              size="sm"
              className="gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              {t('leadList.retrySelected', { count: selected.size })}
            </Button>
          )}
          <Button
            onClick={() => fetchLeads()}
            disabled={loading}
            variant="outline"
            size="sm"
            className="gap-1.5"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            {t('mappingsExtra.refresh')}
          </Button>
        </div>
      </div>

      {leads.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <p className="text-sm">{t('leadList.noLeads')}</p>
        </div>
      ) : (
        <>
          <div className="rounded-lg border divide-y max-h-[500px] overflow-y-auto">
            {unprocessedCount > 0 && (
              <div className="flex items-center gap-2 p-2 bg-muted/30 sticky top-0 z-10">
                <Checkbox
                  checked={selected.size === unprocessedCount && unprocessedCount > 0}
                  onCheckedChange={toggleSelectAll}
                />
                <span className="text-xs text-muted-foreground">{t('leadList.selectAll')}</span>
              </div>
            )}
            {leads.map((lead) => {
              const isExpanded = expandedLead === lead.leadgen_id;
              const isRetrying = retrying.has(lead.leadgen_id);
              const result = retryResults[lead.leadgen_id];

              return (
                <div key={lead.leadgen_id} className="hover:bg-muted/30 transition-colors">
                  <div className="flex items-center gap-3 p-3">
                    {lead.status === 'unprocessed' && (
                      <Checkbox
                        checked={selected.has(lead.leadgen_id)}
                        onCheckedChange={() => toggleSelect(lead.leadgen_id)}
                      />
                    )}
                    {lead.status === 'processed' && (
                      <div className="w-[16px] flex justify-center">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                      </div>
                    )}

                    <button
                      type="button"
                      className="flex-1 min-w-0 text-left"
                      onClick={() => setExpandedLead(isExpanded ? null : lead.leadgen_id)}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium truncate">{getFieldPreview(lead)}</span>
                        {lead.status === 'processed' ? (
                          <Badge className="bg-green-100 text-green-800 text-[10px] shrink-0">
                            {t('leadList.processed')}
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-[10px] shrink-0">
                            {t('leadList.unprocessed')}
                          </Badge>
                        )}
                        {result === 'success' && (
                          <Badge className="bg-green-100 text-green-800 text-[10px] shrink-0">
                            {t('leadList.retrySuccess')}
                          </Badge>
                        )}
                        {result === 'error' && (
                          <Badge className="bg-red-100 text-red-800 text-[10px] shrink-0">
                            {t('leadList.retryFailed')}
                          </Badge>
                        )}
                        {unified && (lead.mapping_profile_name || lead.facebook_form_id) && (
                          <Badge variant="outline" className="text-[10px] font-normal shrink-0 max-w-[180px] truncate">
                            {lead.mapping_profile_name || '—'}
                            {lead.facebook_form_id ? ` · ${lead.facebook_form_id}` : ''}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                        <span className="text-xs text-muted-foreground">
                          {lead.created_time
                            ? new Date(lead.created_time).toLocaleString(dateLocale)
                            : '—'}
                        </span>
                        <span className="text-xs font-mono text-muted-foreground truncate">{lead.leadgen_id}</span>
                      </div>
                    </button>

                    <div className="flex items-center gap-1 shrink-0">
                      {!result && (
                        <Button
                          type="button"
                          variant={lead.status === 'processed' ? 'outline' : 'ghost'}
                          size="sm"
                          disabled={isRetrying}
                          onClick={(e) => {
                            e.stopPropagation();
                            retryLead(lead.leadgen_id, lead.status === 'processed');
                          }}
                          className="h-8 px-2 gap-1"
                          title={
                            lead.status === 'processed' ? t('leadList.reprocessHint') : undefined
                          }
                        >
                          {isRetrying ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <RotateCcw className="w-3.5 h-3.5" />
                          )}
                          {lead.status === 'processed' && (
                            <span className="text-[11px]">{t('leadList.reprocess')}</span>
                          )}
                        </Button>
                      )}
                      <button
                        type="button"
                        onClick={() => setExpandedLead(isExpanded ? null : lead.leadgen_id)}
                        className="p-1 hover:bg-muted rounded"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-3 pb-3 border-t bg-muted/20">
                      <div className="pt-2 space-y-1">
                        {lead.field_data.map((f, i) => (
                          <div key={i} className="flex items-start gap-2 text-xs">
                            <span className="font-mono text-muted-foreground w-[140px] shrink-0 truncate">
                              {f.name}
                            </span>
                            <span className="text-foreground break-all">{f.values.join(', ')}</span>
                          </div>
                        ))}
                        {lead.deal_id && (
                          <div className="flex items-start gap-2 text-xs pt-1 border-t mt-1">
                            <span className="font-mono text-muted-foreground w-[140px] shrink-0">deal_id</span>
                            <span className="font-mono text-foreground">{lead.deal_id}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {((unified && hasMoreUnified) || (!unified && nextCursor)) && (
            <div className="flex justify-center pt-2">
              <Button
                onClick={() =>
                  unified
                    ? fetchLeads({ cursorsByForm: afterByForm })
                    : fetchLeads({ singleCursor: nextCursor! })
                }
                disabled={loading}
                variant="outline"
                size="sm"
                className="gap-1.5"
              >
                {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                {t('leadList.loadMore')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
